import type { Dispatch, SetStateAction } from 'react'
import { Ban, Play, RotateCcw } from 'lucide-react'
import type { AppState, MonteCarloResult } from '../types'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { TolerancePlots } from './Plots'
import { EmptyState, Metric, ModelBadge, Notice, NumberField, Section, SelectField, formatNumber } from './ui'

interface TolerancesTabProps {
  state: AppState
  setState: Dispatch<SetStateAction<AppState>>
  result: MonteCarloResult | null
  stale: boolean
  runState: {
    kind: 'simulation' | 'monte-carlo' | null
    running: boolean
    progress: number
    message: string
    error: string | null
  }
  onRun: () => void
  onCancel: () => void
}

export function TolerancesTab({
  state,
  setState,
  result,
  stale,
  runState,
  onRun,
  onCancel,
}: TolerancesTabProps) {
  const settings = state.toleranceSettings
  const isRunning = runState.running && runState.kind === 'monte-carlo'
  const modeMismatch =
    (settings.mode === 'analytical-paraxial' && state.lens.mode !== 'thin') ||
    (settings.mode === 'geometric-ray' && state.lens.mode !== 'thick')
  const update = (patch: Partial<AppState['toleranceSettings']>) =>
    setState((current) => ({
      ...current,
      toleranceSettings: { ...current.toleranceSettings, ...patch },
    }))

  return (
    <div className="tab-stack">
      <div className="result-header">
        <div>
          <ModelBadge>Monte Carlo tolerance result</ModelBadge>
          <h1>Tolerances</h1>
          <p>Reproducible independent uncertainty draws evaluated against the visible selected criteria.</p>
        </div>
        {isRunning ? (
          <button className="danger-button" onClick={onCancel}><Ban size={16} /> Cancel trials</button>
        ) : (
          <button className="primary-button" onClick={onRun} disabled={modeMismatch}><Play size={16} /> Run tolerance analysis</button>
        )}
      </div>

      {modeMismatch ? <Notice tone="warning">Analytical/paraxial trials require the active ideal thin-lens model. Geometric-ray trials require the active circular thick-lens model.</Notice> : null}
      {runState.error && runState.kind === 'monte-carlo' ? <Notice tone="danger" title="Tolerance run failed">{runState.error}</Notice> : null}
      {isRunning ? (
        <div className="progress-panel" role="status" aria-live="polite">
          <div className="progress-label"><strong>{runState.message}</strong><span>{Math.round(runState.progress * 100)}%</span></div>
          <progress value={runState.progress} max={1} />
          <span>Seeded trials run in a Web Worker. Results are reproducible for the same inputs and seed.</span>
        </div>
      ) : null}

      <Section title="Trial configuration" description="Normal entries are one standard deviation. Uniform entries are symmetric half-ranges.">
        <div className="form-grid four">
          <NumberField label="Number of trials" value={settings.trials} onChange={(value) => update({ trials: Math.round(Math.max(10, Math.min(settings.mode === 'geometric-ray' ? 2000 : 10000, value ?? 10))) })} min={10} max={settings.mode === 'geometric-ray' ? 2000 : 10000} step={10} />
          <NumberField label="Random seed" value={settings.seed} onChange={(value) => update({ seed: Math.round(value ?? 0) })} step={1} />
          <SelectField label="Evaluation mode" value={settings.mode} onChange={(value) => update({ mode: value as AppState['toleranceSettings']['mode'] })} options={[
            { value: 'analytical-paraxial', label: 'Fast analytical / paraxial (thin lens)' },
            { value: 'geometric-ray', label: 'Slower geometric ray (thick lens)' },
          ]} />
          <SelectField label="Default lens/global distribution" value={settings.defaultDistribution} onChange={(value) => update({ defaultDistribution: value as AppState['toleranceSettings']['defaultDistribution'] })} options={[
            { value: 'normal', label: 'Normal, entered σ' },
            { value: 'uniform', label: 'Uniform, entered ± half-range' },
          ]} />
          <NumberField label="Target-distance uncertainty" unit="mm σ / ±" value={settings.targetDistanceToleranceMm} onChange={(value) => update({ targetDistanceToleranceMm: Math.max(0, value ?? 0) })} min={0} />
          <NumberField label="Source-aperture uncertainty" unit="mm σ / ±" value={settings.sourceApertureToleranceMm} onChange={(value) => update({ sourceApertureToleranceMm: Math.max(0, value ?? 0) })} min={0} />
          <NumberField label="Pupil-position uncertainty" unit="mm σ / ±" value={settings.pupilPositionToleranceMm} onChange={(value) => update({ pupilPositionToleranceMm: Math.max(0, value ?? 0) })} min={0} />
        </div>
        <Notice>
          Layer thickness and index distributions are configured per row in Setup. Lens pitch, focal/radius, thickness, index, and phase uncertainties use the active lens tolerances. Parameters are sampled independently.
        </Notice>

        <div className="subsection-heading"><h3>Criteria included in each trial</h3><span>A trial succeeds only when every enabled item succeeds.</span></div>
        <div className="check-grid">
          {[
            ['crosstalk', 'Crosstalk'],
            ['zoneCenter', 'Zone-center error'],
            ['throughput', 'Relative throughput'],
            ['phaseDrift', 'Accumulated phase drift'],
            ['viewingRange', 'Viewing-distance range'],
          ].map(([key, label]) => (
            <label className="inline-check" key={key}>
              <input type="checkbox" checked={settings.includeCriteria[key as keyof typeof settings.includeCriteria]} onChange={(event) => update({ includeCriteria: { ...settings.includeCriteria, [key]: event.target.checked } })} />
              {label}
            </label>
          ))}
        </div>
      </Section>

      {!result ? (
        <EmptyState title="No tolerance result yet">Enter uncertainties, select the criteria to test, then run seeded trials. The success fraction is conditional on exactly those assumptions.</EmptyState>
      ) : (
        <>
          {stale ? <Notice tone="warning" title="Result is stale">An input or uncertainty changed after this run. <button className="text-button" onClick={onRun}><RotateCcw size={13} /> Re-run</button></Notice> : null}
          <Section title="Conditional trial result" description="This is not an unconditional probability that a product will work. It is the fraction of displayed uncertainty draws meeting the displayed criteria.">
            <div className="section-model-row"><ModelBadge>{result.model}</ModelBadge><span>Underlying evaluation: {result.evaluationModel}; seed {result.seed}</span></div>
            <div className="metric-grid compact-top">
              <Metric label="Trials meeting all criteria" value={formatNumber(result.successRate * 100, 2)} unit="%" />
              <Metric label="Valid trials" value={result.validTrials.toLocaleString()} />
              <Metric label="Invalid physical draws" value={result.invalidTrials.toLocaleString()} />
              <Metric label="Elapsed worker time" value={formatNumber(result.elapsedMs, 0)} unit="ms" />
            </div>
            <ul className="assumption-list">{result.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}</ul>
          </Section>

          <Section title="Distributions and percentiles" description="P05, P50, and P95 use linearly interpolated empirical quantiles over finite valid results.">
            <TolerancePlots result={result} />
            <HorizontalScrollRegion className="table-scroll compact-top" label="Tolerance distribution percentiles">
              <table className="data-table">
                <thead><tr><th>Metric</th><th>P05</th><th>P50</th><th>P95</th><th>Unit</th></tr></thead>
                <tbody>
                  <tr><td>Crosstalk</td><td>{formatNumber(result.percentiles.crosstalk.p05, 4)}</td><td>{formatNumber(result.percentiles.crosstalk.p50, 4)}</td><td>{formatNumber(result.percentiles.crosstalk.p95, 4)}</td><td>fraction</td></tr>
                  <tr><td>Zone-center error</td><td>{formatNumber(result.percentiles.zoneCenterErrorMm.p05, 3)}</td><td>{formatNumber(result.percentiles.zoneCenterErrorMm.p50, 3)}</td><td>{formatNumber(result.percentiles.zoneCenterErrorMm.p95, 3)}</td><td>mm</td></tr>
                  <tr><td>Predicted viewing distance</td><td>{formatNumber(result.percentiles.predictedViewingDistanceMm.p05, 2)}</td><td>{formatNumber(result.percentiles.predictedViewingDistanceMm.p50, 2)}</td><td>{formatNumber(result.percentiles.predictedViewingDistanceMm.p95, 2)}</td><td>mm</td></tr>
                  <tr><td>Accumulated drift</td><td>{formatNumber(result.percentiles.driftSourceCells.p05, 3)}</td><td>{formatNumber(result.percentiles.driftSourceCells.p50, 3)}</td><td>{formatNumber(result.percentiles.driftSourceCells.p95, 3)}</td><td>source cells</td></tr>
                </tbody>
              </table>
            </HorizontalScrollRegion>
          </Section>

          <Section title="Failure association" description="Spearman rank correlation between each sampled parameter and normalized worst-criterion severity. Correlation does not establish causation.">
            {result.parameterRanking.length ? (
              <HorizontalScrollRegion className="table-scroll" label="Failure association ranking">
                <table className="data-table"><thead><tr><th>Rank</th><th>Parameter</th><th>Spearman ρ</th><th>Interpretation</th></tr></thead><tbody>{result.parameterRanking.map((item, index) => <tr key={item.parameter}><td>{index + 1}</td><td>{item.parameter}</td><td>{formatNumber(item.spearmanRho, 4)}</td><td>{item.association}</td></tr>)}</tbody></table>
              </HorizontalScrollRegion>
            ) : <Notice>Association is unavailable when too few valid trials share a parameter series or the result is constant.</Notice>}
          </Section>
        </>
      )}
    </div>
  )
}
